import { useEffect, useState } from "react";
import moment from "moment";
import { toast } from "sonner";
import { Globe, RefreshCw, Smartphone, Download, AlertTriangle } from "lucide-react";
import {
  getPlatformClinics,
  getPlatformClinicSettings,
  updatePlatformClinicFeature,
  updatePlatformClinicModules,
  getAppReleases,
  updateCurrentAppRelease,
} from "../../api/api";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { Badge } from "../../components/ui/badge";
import { MODULE_GROUPS } from "../../lib/modules";

function SectionCard({ title, description, children }) {
  return (
    <div className="rounded-lg border border-border bg-card p-5">
      <h3 className="text-sm font-semibold">{title}</h3>
      {description && <p className="mt-1 text-xs text-muted-foreground">{description}</p>}
      <div className="mt-4 space-y-4">{children}</div>
    </div>
  );
}

// One deploy.sh stamp (see build_info in deploy.sh): when it shipped, from
// which commit, and a warning when uncommitted changes went out with it.
function BuildRow({ label, build }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border px-3 py-2.5 text-sm">
      <span className="font-medium">{label}</span>
      {build ? (
        <span className="flex flex-wrap items-center gap-2 text-muted-foreground">
          <span title={build.deployed_at}>{moment(build.deployed_at).format("D MMM YYYY, h:mm A")}</span>
          <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{build.commit}</code>
          {build.uncommitted_changes && (
            <Badge variant="warning" className="gap-1" title="Deployed with changes that aren't committed to git">
              <AlertTriangle className="h-3 w-3" /> uncommitted
            </Badge>
          )}
        </span>
      ) : (
        <span className="text-muted-foreground">Unknown — not deployed with version stamping yet</span>
      )}
    </div>
  );
}

function ClinicDetail({ clinic }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    getPlatformClinicSettings(clinic.key)
      .then((res) => setSettings(res.data))
      .catch((err) => toast.error(err?.message || `Could not reach ${clinic.name}.`))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [clinic.key]);

  const toggleClickToCall = async (value) => {
    setSaving(true);
    try {
      await updatePlatformClinicFeature(clinic.key, { click_to_call_enabled: value });
      toast.success(`Click-to-call ${value ? "enabled" : "disabled"} for ${clinic.name}.`);
      load();
    } catch (err) {
      toast.error(err?.message || "Could not update.");
    } finally {
      setSaving(false);
    }
  };

  const toggleModule = async (mod, value) => {
    setSaving(true);
    try {
      await updatePlatformClinicModules(clinic.key, { [mod.key]: value });
      toast.success(`${mod.label} ${value ? "shown" : "hidden"} for ${clinic.name}.`);
      load();
    } catch (err) {
      toast.error(err?.message || "Could not update.");
    } finally {
      setSaving(false);
    }
  };

  // An older clinic backend has no `modules` in its settings yet — the
  // switches would save nowhere, so say so instead of showing them.
  const modules = settings?.features?.modules;

  return (
    <SectionCard title={clinic.name} description={clinic.base_url}>
      <div className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Deployed versions</p>
        <BuildRow label="Backend" build={clinic.backend_build} />
        <BuildRow label="Dashboard" build={clinic.dashboard_build} />
      </div>
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : !settings ? (
        <p className="text-sm text-muted-foreground">Could not load this clinic's settings.</p>
      ) : (
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={settings.features?.click_to_call_enabled !== false}
            onCheckedChange={toggleClickToCall}
            disabled={saving}
          />
          Click-to-call enabled
        </label>
      )}
      {!loading && settings && (
        <div className="space-y-3 border-t border-border pt-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Sidebar modules</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Unticked modules disappear from this clinic's sidebar and their pages can't be opened. Dashboard, Settings and User
              Master are always on.
            </p>
          </div>
          {!modules ? (
            <p className="text-sm text-muted-foreground">This clinic's backend is too old for module switches — deploy it first.</p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {MODULE_GROUPS.map((group) => (
                <div key={group.label} className="space-y-2">
                  <p className="text-sm font-medium">{group.label}</p>
                  {group.modules.map((mod) => (
                    <label key={mod.key} className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={modules[mod.key] !== false}
                        onCheckedChange={(v) => toggleModule(mod, !!v)}
                        disabled={saving}
                      />
                      {mod.label}
                    </label>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </SectionCard>
  );
}

const formatSize = (bytes) => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

// Android APK releases (the same files `./deploy.sh --apk` publishes). The
// current release, its notes and the minimum version are what installed
// apps read on every launch — see medipro-mobile/lib/app-update.ts.
function AppReleases() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [version, setVersion] = useState("");
  const [notes, setNotes] = useState("");
  const [minVersion, setMinVersion] = useState("");

  const load = () => {
    setLoading(true);
    getAppReleases()
      .then((res) => {
        setData(res.data);
        const current = res.data.current;
        const currentVersion = current?.version || res.data.releases[0]?.version || "";
        setVersion(currentVersion);
        setNotes(res.data.releases.find((r) => r.version === currentVersion)?.notes || current?.notes || "");
        setMinVersion(current?.min_version || "");
      })
      .catch((err) => toast.error(err?.message || "Could not load app releases."))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const pickVersion = (v) => {
    setVersion(v);
    setNotes(data?.releases.find((r) => r.version === v)?.notes || "");
    // A minimum newer than the chosen release would lock every phone out.
    if (minVersion && data?.releases.findIndex((r) => r.version === minVersion) < data?.releases.findIndex((r) => r.version === v)) {
      setMinVersion("");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await updateCurrentAppRelease({ version, notes, min_version: minVersion || null });
      toast.success(`v${version} is now the current release.`);
      load();
    } catch (err) {
      toast.error(err?.message || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  const current = data?.current;
  const releases = data?.releases || [];
  // Only versions at or below the chosen release can be the minimum.
  const minOptions = releases.filter((r) => releases.indexOf(r) >= releases.findIndex((x) => x.version === version));
  const isRollback = current?.version && version && releases.findIndex((r) => r.version === version) > releases.findIndex((r) => r.version === current.version);

  return (
    <SectionCard
      title={
        <span className="flex items-center gap-2">
          <Smartphone className="h-4 w-4" /> Android App Releases
        </span>
      }
      description="Every published APK. The current release is what installed apps are offered; phones below the minimum version are blocked until they update."
    >
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : !data?.available ? (
        <p className="text-sm text-muted-foreground">Release folder not found on this server ({data?.dir}).</p>
      ) : releases.length === 0 ? (
        <p className="text-sm text-muted-foreground">No APKs published yet — run ./deploy.sh --apk.</p>
      ) : (
        <div className="grid items-start gap-5 lg:grid-cols-[1fr_360px]">
          <div className="overflow-hidden rounded-md border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">Version</th>
                  <th className="px-3 py-2 font-medium">Published</th>
                  <th className="px-3 py-2 font-medium">Size</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {releases.map((r) => (
                  <tr key={r.version} className="border-t border-border">
                    <td className="px-3 py-2.5">
                      <span className="flex items-center gap-2 font-medium">
                        v{r.version}
                        {r.current && <Badge variant="secondary">Current</Badge>}
                        {current?.min_version === r.version && <Badge variant="warning">Minimum</Badge>}
                      </span>
                      {r.notes && <p className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">{r.notes}</p>}
                    </td>
                    <td className="px-3 py-2.5 text-muted-foreground">{moment(r.uploaded_at).format("D MMM YYYY, h:mm A")}</td>
                    <td className="px-3 py-2.5 text-muted-foreground">{formatSize(r.size)}</td>
                    <td className="px-3 py-2.5 text-right">
                      <a href={r.url} className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                        <Download className="h-3.5 w-3.5" /> APK
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="space-y-3 rounded-md border border-border p-4">
            <label className="block text-sm font-medium">
              Current release
              <select
                value={version}
                onChange={(e) => pickVersion(e.target.value)}
                className="mt-1 h-9 w-full rounded-md border border-input bg-card px-2 text-sm"
              >
                {releases.map((r) => (
                  <option key={r.version} value={r.version}>
                    v{r.version}
                    {r.current ? " (current)" : ""}
                  </option>
                ))}
              </select>
            </label>
            {isRollback && (
              <p className="rounded-md bg-warning/10 px-3 py-2 text-xs text-warning">
                Rollback: new installs and the download link get v{version}. Phones already on v{current.version} keep it —
                apps never update downwards.
              </p>
            )}
            <label className="block text-sm font-medium">
              What's new (shown in the update prompt)
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={5}
                placeholder="e.g. Doctor attendance, faster check-in, bug fixes"
                className="mt-1 w-full rounded-md border border-input bg-card px-2.5 py-2 text-sm"
              />
            </label>
            <label className="block text-sm font-medium">
              Minimum version
              <select
                value={minVersion}
                onChange={(e) => setMinVersion(e.target.value)}
                className="mt-1 h-9 w-full rounded-md border border-input bg-card px-2 text-sm"
              >
                <option value="">None — updates are optional</option>
                {minOptions.map((r) => (
                  <option key={r.version} value={r.version}>
                    v{r.version} — block anything older
                  </option>
                ))}
              </select>
              <span className="mt-1 block text-xs font-normal text-muted-foreground">
                Phones below this see "Update required" with no way to skip.
              </span>
            </label>
            <Button className="w-full" onClick={save} disabled={saving || !version}>
              {saving ? "Saving…" : "Save release settings"}
            </Button>
          </div>
        </div>
      )}
    </SectionCard>
  );
}

export default function PlatformAdmin() {
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedKey, setSelectedKey] = useState(null);

  const load = () => {
    setLoading(true);
    getPlatformClinics()
      .then((res) => {
        setClinics(res.data);
        setSelectedKey((prev) => prev || res.data[0]?.key || null);
      })
      .catch((err) => toast.error(err?.message || "Could not load clinics."))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const selected = clinics.find((c) => c.key === selectedKey);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="flex items-center gap-2 font-serif text-2xl font-semibold">
            <Globe className="h-5 w-5" /> Platform Admin
          </h2>
          <p className="text-sm text-muted-foreground">Aivexa-internal — view and control every MediPro clinic deployment</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-[280px_1fr]">
        <SectionCard title="Clinics">
          {loading && clinics.length === 0 ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : clinics.length === 0 ? (
            <p className="text-sm text-muted-foreground">No clinics configured (PLATFORM_CLINICS is empty).</p>
          ) : (
            <div className="space-y-1">
              {clinics.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => setSelectedKey(c.key)}
                  className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm transition-colors ${
                    selectedKey === c.key ? "bg-primary text-primary-foreground" : "hover:bg-accent"
                  }`}
                >
                  <span className="truncate">{c.name}</span>
                  <Badge variant={c.status === "up" ? "secondary" : "destructive"}>{c.status}</Badge>
                </button>
              ))}
            </div>
          )}
        </SectionCard>

        {selected ? <ClinicDetail key={selected.key} clinic={selected} /> : (
          <SectionCard title="No clinic selected">
            <p className="text-sm text-muted-foreground">Select a clinic on the left.</p>
          </SectionCard>
        )}
      </div>

      <AppReleases />
    </div>
  );
}
